import { useLocation, Link } from "react-router";
import { useRootFoldersQuery, useCurrentFolderQuery } from "@/features/file-manager/hooks/useFileManagerQueries";
import type { FolderResponse } from "@/features/file-manager/services/file-manager.service.types";
import { FILE_MANAGER_FOLDER_PATH } from "@/shared/configs/routes.config";
import { cn } from "@/shared/lib/utils";
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/shared/ui/Sidebar";

const FileTreeNavigation = () => {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();

  // Get root folders from API
  const { data: rootFoldersData } = useRootFoldersQuery();
  const rootFolders = (rootFoldersData?.folders || []).sort((a: FolderResponse, b: FolderResponse) => a.name.localeCompare(b.name));

  // Extract current folder ID from URL
  const getCurrentFolderId = () => {
    const match = location.pathname.match(/\/gestionnaire-fichiers\/dossier\/([^/]+)/);
    return match ? match[1] : null;
  };

  const currentFolderId = getCurrentFolderId();
  // Get current folder data to check its parent hierarchy
  const { data: currentFolderData } = useCurrentFolderQuery(currentFolderId);

  const isActiveRoute = (rootFolderId: string) => {
    // Si on est exactement dans ce dossier root
    if (currentFolderId === rootFolderId) {
      return true;
    }

    // Si on est dans un sous-dossier, vérifier la hiérarchie
    if (currentFolderData && currentFolderId) {
      // Fonction récursive pour remonter la hiérarchie via le path
      const isDescendantOfRoot = (
        folderData: {
          parent_id?: string | null
          path?: string
        },
        targetRootId: string,
      ): boolean => {
        // Si le parent direct est le dossier root recherché
        if (folderData.parent_id === targetRootId) {
          return true;
        }

        // Vérifier via le path si possible
        if (folderData.path && rootFolders.length > 0) {
          const rootFolder = rootFolders.find((rf: FolderResponse) => rf.id.toString() === targetRootId);
          if (rootFolder?.name && folderData.path.startsWith(rootFolder.name + "/")) {
            return true;
          }
        }

        return false;
      };

      return isDescendantOfRoot(currentFolderData, rootFolderId);
    }

    return false;
  };

  if (collapsed) {
    return null;
  }

  return (
    <SidebarMenuSub>
      {/* Dossiers de premier niveau seulement */}
      {rootFolders.map((folder: FolderResponse) => {
        const isActive = isActiveRoute(folder.id.toString());

        return (
          <SidebarMenuSubItem key={folder.id}>
            <SidebarMenuSubButton
              asChild
              className={cn(
                "w-full py-1 h-7 text-sm",
                isActive && "font-bold text-black",
              )}
            >
              <Link to={FILE_MANAGER_FOLDER_PATH.replace(":folderId", folder.id.toString())}>
                {folder.name}
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
    </SidebarMenuSub>
  );
};

export default FileTreeNavigation;
